// The transfer motion's browser half (#6772, epic #6552): what the page reads off the DOM the
// moment a move is ACCEPTED, handed to `playTransfer` (`./transfer-motion.ts`). It runs inside
// `MovePage`'s send, on the 201 itself and before the re-read, while the moved rows are still on
// their source card: the echo is a copy of each row as it stood, its rect where it stood.
//
// Rows are found by their move key on the source card (`data-move-row`, the key every grip and
// Move button carries); an item with no row on screen (a job with no grip, a card scrolled away)
// starts its arc from the source card itself. Under reduced motion nothing is read or drawn.

import { t } from '../i18n';
import type { AcceptedMove } from './move-page';
import {
  browserMotionSeam,
  type MotionSeam,
  playTransfer,
  type TransferBox,
  type TransferPlay,
} from './transfer-motion';

const cardEl = (id: string): HTMLElement | null =>
  document.querySelector<HTMLElement>(`[data-move-card="${CSS.escape(id)}"]`);

const boxOf = (el: Element): TransferBox => {
  const r = el.getBoundingClientRect();
  return {
    left: r.left,
    top: r.top,
    right: r.right,
    bottom: r.bottom,
    width: r.width,
    height: r.height,
  };
};

/** Play the motion for one accepted move; null when nothing is drawn. `onClosed`: it ended. */
export function playAccepted(
  a: AcceptedMove,
  nameOf: (id: string) => string,
  seam: MotionSeam = browserMotionSeam(),
  onClosed: () => void = () => {},
): TransferPlay | null {
  if (seam.reduced()) return null;
  const req = a.request;
  const source = cardEl(req.sourceId);
  const target = cardEl(req.targetId);
  const rows: { node: Element | null; rect: TransferBox }[] = req.items.flatMap((it) => {
    const key = `${req.sourceId}|${it.kind}:${it.id}`;
    const row = source?.querySelector(`[data-move-row="${CSS.escape(key)}"]`) ?? null;
    return row === null ? [] : [{ node: row.cloneNode(true) as Element, rect: boxOf(row) }];
  });
  if (rows.length === 0 && source !== null) rows.push({ node: null, rect: boxOf(source) });
  if (rows.length === 0) return null;
  const n = (a.accepted.batch.items ?? []).length || req.items.length;
  const from = nameOf(req.sourceId);
  return playTransfer(
    {
      rows,
      source,
      target,
      targetRect: target === null ? null : boxOf(target),
      viewport: { width: window.innerWidth, height: window.innerHeight },
      tag:
        n > 1
          ? t('fleet.move.fx_receiving_many', { count: n, name: from })
          : t('fleet.move.fx_receiving', { name: from }),
      chip: t('fleet.move.fx_chip', { name: nameOf(req.targetId) }),
    },
    seam,
    onClosed,
  );
}
